import { Box, Typography } from '@mui/material';
import { useCallback, useMemo, useRef } from 'react';

import { FilterTextInput } from '@work4all/components/lib/components/entity-picker/components/FilterTextInput';
import { SelectableTree } from '@work4all/components/lib/dataDisplay/tree/SelectableTree';
import { useTreeFilter } from '@work4all/components/lib/hooks/useTreeFilter';

import { Category } from '@work4all/models/lib/Classes/Category.entity';
import { CategoryClass } from '@work4all/models/lib/Classes/CategoryClass.entity';
import { Filters } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { isCategoryClass, useCategoryClasses } from './use-category-class';

export interface CategoryPickerProps {
  value: Category[] | null;
  onChange: (value: Category[]) => void;
  /** You can override the default entity type if there is a separate GraphQL
   * type and query to get the categories you want.
   *
   * @default Entities.categoryClass
   * */
  entity?: Entities;
  filter?: Filters | null;
  multiple?: boolean;
  expanded?: string[];
  onNodeToggle?: (event: React.SyntheticEvent, nodeIds: string[]) => void;
  /** Show search input field */
  showSearch?: boolean;
}

export function CategoryPicker(props: CategoryPickerProps) {
  const {
    value,
    onChange,
    entity = Entities.categoryClass,
    filter,
    multiple = true,
    expanded,
    onNodeToggle,
    showSearch = true,
  } = props;

  const selectableTreeRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    treeData,
    originalById,
    categoryClasses: allCategoryClasses,
  } = useCategoryClasses(filter, entity);

  // Flatten all categories and category classes for search
  const allSearchableItems = useMemo(() => {
    const items: (Category | CategoryClass)[] = [];

    allCategoryClasses.forEach((categoryClass) => {
      items.push(categoryClass);
      if (categoryClass.categoryList) {
        items.push(...categoryClass.categoryList);
      }
    });

    return items;
  }, [allCategoryClasses]);

  // Use custom hook for filtering functionality
  const { filteredData: filteredTreeData, searchProps } = useTreeFilter(
    treeData,
    allSearchableItems,
    {
      showSearch,
      filterPredicate: useCallback((item, searchString) => {
        const searchLower = searchString.toLowerCase();
        return item.name?.toLowerCase().includes(searchLower) ?? false;
      }, []),
      itemToTreeNode: useCallback(
        (item) => ({
          id: item.id.toString(),
          label: (
            <Typography component="div">
              <span>{item.name}</span>
              {isCategoryClass(item) && item.categoryList?.length > 0 && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  component="span"
                  sx={{ ml: 1 }}
                >
                  ({item.categoryList.length} categories)
                </Typography>
              )}
            </Typography>
          ),
          children: undefined, // Flat list when searching
        }),
        []
      ),
    }
  );

  const selected = useMemo<string[]>(() => {
    if (!value) {
      return [];
    }

    const selectedIds = value.map((item) => item.id.toString());

    // HACK We need to manually add the ids of fully selected group nodes here,
    // so that MUI considers them selected for the purposes of selection state
    // management. Otherwise there will be no way to deselect them.
    //
    // This should be removed after reworking SelectableTree to handle all
    // selection logic internally.

    const selectedCategoryClassIds = allCategoryClasses
      .filter((categoryClass) => {
        return (
          categoryClass.categoryList.length &&
          categoryClass.categoryList.every((category) =>
            selectedIds.includes(category.id.toString())
          )
        );
      })
      .map((categoryClass) => categoryClass.id.toString());

    return [...selectedIds, ...selectedCategoryClassIds];
  }, [value, allCategoryClasses]);

  // This is for the most part copied from CustomerGroupPicker and will be
  // removed after SelectableTree component is reworked.

  const handleChange = (newSelected: string[] = []) => {
    const removedIds = selected.filter((id) => !newSelected.includes(id));
    const addedIds = newSelected.filter((id) => !selected.includes(id));

    function flatMapCategories(id: string): Category[] {
      const original = originalById.get(id);

      if (!original) {
        return [];
      }

      if (isCategoryClass(original) && original.categoryList.length) {
        return original.categoryList;
      } else {
        return [original];
      }
    }

    const removed = removedIds.flatMap((id) => flatMapCategories(id));
    const added = addedIds.flatMap((id) => flatMapCategories(id));

    const draft = new Set(value);

    for (const category of removed) {
      draft.delete(category);
    }

    for (const category of added) {
      draft.add(category);
    }

    const asArray = [...draft];

    onChange(asArray);
  };

  //Adding 2px margin to prevent unnecessary scrollbar in picker popovers
  return (
    <Box mb="2px">
      {showSearch && (
        <div style={{ paddingLeft: '0.5rem' }}>
          <FilterTextInput
            ref={inputRef}
            autoFocus
            value={searchProps.value}
            onChange={searchProps.onChange}
            placeholder={searchProps.placeholder}
            onKeyDown={searchProps.onKeyDown}
          />
        </div>
      )}
      <div ref={selectableTreeRef}>
        <SelectableTree
          multiple={multiple}
          selectable={multiple ? 'all' : 'leaf'}
          data={filteredTreeData}
          selected={selected}
          onChange={(value) => {
            Array.isArray(value) ? handleChange(value) : handleChange([value]);
          }}
          expanded={expanded}
          onNodeToggle={onNodeToggle}
        />
      </div>
    </Box>
  );
}
